import {
  CHUNK_FILE_COUNT,
  CHUNK_MAX_BYTES,
  MAX_BATCH_FILES,
  MAX_FILE_BYTES,
} from "../constants/courseCertificateUpload";
import { ERROR_CODES } from "../constants/errorCodes";
import { isPdfFile } from "../hooks/useFileDropZone";
import type { CourseCertificateUploadError } from "../models/courseCertificate";

/** The parts of File this module actually reads. Generic over the concrete
 * file type so callers get their own File back out, while tests can pass
 * plain metadata objects (the test environment is node, with no File). */
export type UploadFileLike = Pick<File, "name" | "size" | "type">;

export type PlannedFile<TFile extends UploadFileLike = UploadFileLike> = {
  file: TFile;
  /** Index in the user's original selection. Survives chunking, so error
   * rows stay unique and stay in the order the user picked. */
  selectionIndex: number;
};

export type UploadChunk<TFile extends UploadFileLike = UploadFileLike> = {
  chunkIndex: number;
  entries: PlannedFile<TFile>[];
};

export type UploadPlan<TFile extends UploadFileLike = UploadFileLike> = {
  chunks: UploadChunk<TFile>[];
  acceptedCount: number;
  preflightErrors: CourseCertificateUploadError[];
};

const duplicateKey = (file: UploadFileLike): string =>
  `${file.name}|${file.size}`;

/** Validates a selection and splits the survivors into upload chunks.
 *
 * Every rejection reuses an existing ERROR_CODES value, so the messages come
 * from errorCodeMessages without a new translation.
 *
 * The duplicate check keys on name + size rather than content: hashing would
 * be exact but needs crypto.subtle, which would make this async and untestable
 * in the node test environment. A same-name-same-size pair that is genuinely
 * two different certificates is rejected here; the server's content hash is
 * the authority for everything that gets through. */
export const planCourseCertificateUpload = <TFile extends UploadFileLike>(
  files: readonly TFile[],
): UploadPlan<TFile> => {
  const preflightErrors: CourseCertificateUploadError[] = [];
  const accepted: PlannedFile<TFile>[] = [];
  const seenKeys = new Set<string>();

  const reject = (
    file: TFile,
    selectionIndex: number,
    errorCode: string,
  ): void => {
    preflightErrors.push({
      id: String(selectionIndex),
      fileName: file.name,
      errorCode,
    });
  };

  files.forEach((file, selectionIndex) => {
    if (!isPdfFile(file)) {
      reject(file, selectionIndex, ERROR_CODES.UNSUPPORTED_FILE_TYPE);

      return;
    }

    if (file.size === 0) {
      reject(file, selectionIndex, ERROR_CODES.EMPTY_FILE);

      return;
    }

    if (file.size > MAX_FILE_BYTES) {
      reject(file, selectionIndex, ERROR_CODES.FILE_TOO_LARGE);

      return;
    }

    const key = duplicateKey(file);
    if (seenKeys.has(key)) {
      reject(file, selectionIndex, ERROR_CODES.DUPLICATE);

      return;
    }

    // Counted against the accepted files, not the raw selection, so rejected
    // files do not eat into the batch allowance.
    if (accepted.length >= MAX_BATCH_FILES) {
      reject(file, selectionIndex, ERROR_CODES.BATCH_LIMIT_EXCEEDED);

      return;
    }

    seenKeys.add(key);
    accepted.push({ file, selectionIndex });
  });

  const chunks: UploadChunk<TFile>[] = [];
  let current: PlannedFile<TFile>[] = [];
  let currentBytes = 0;

  const flush = (): void => {
    if (current.length === 0) {
      return;
    }

    chunks.push({ chunkIndex: chunks.length, entries: current });
    current = [];
    currentBytes = 0;
  };

  for (const entry of accepted) {
    const exceedsCount = current.length >= CHUNK_FILE_COUNT;
    const exceedsBytes =
      current.length > 0 && currentBytes + entry.file.size > CHUNK_MAX_BYTES;

    if (exceedsCount || exceedsBytes) {
      flush();
    }

    current.push(entry);
    currentBytes += entry.file.size;
  }

  flush();

  return { chunks, acceptedCount: accepted.length, preflightErrors };
};
